import { useCallback, useState, type ReactNode } from "react";
import { matchPath, useLocation } from "react-router-dom";
import {
  decodeRouteSegment,
  joinPath,
  useRoutedDialog,
  useRoutedOverlayBase,
} from "shared-ui/src/hooks/routedOverlays";
import { EMAIL_ORDER_INVOICES_SEGMENT } from "./emailOrderInvoicesRouteSegments";
import EmailOrderInvoicesDialog from "./EmailOrderInvoicesDialog";

const DIALOG_PATH = `${EMAIL_ORDER_INVOICES_SEGMENT}/:orderKey`;

export interface EmailOrderInvoicesTarget {
  salesOrder?: string;
  biOrderId?: number;
  custPONumber?: string;
}

const isSameTarget = (
  a: EmailOrderInvoicesTarget | null,
  b: EmailOrderInvoicesTarget | null,
) =>
  a === b ||
  (a != null &&
    b != null &&
    a.salesOrder === b.salesOrder &&
    a.biOrderId === b.biOrderId &&
    a.custPONumber === b.custPONumber);

/**
 * The order key in the URL while the dialog's route is open under `basePath` (by default the
 * `RoutedOverlayBase`), so the host can resolve which order to email before opening it.
 */
export function useEmailOrderInvoicesRouteKey(
  basePath?: string | null,
): string | undefined {
  const contextBase = useRoutedOverlayBase();
  const base = basePath === undefined ? contextBase : basePath;
  const { pathname } = useLocation();
  if (base == null) {
    return undefined;
  }
  const key = matchPath(
    { path: joinPath(base, DIALOG_PATH), end: true },
    pathname,
  )?.params.orderKey;
  return key ? decodeRouteSegment(key) : undefined;
}

interface UseRoutedEmailOrderInvoicesDialogOptions {
  /** Pathname the dialog opens under. Defaults to the `RoutedOverlayBase`. */
  basePath?: string | null;
  /** The order for the key in the URL, or null when this host would not offer to email it. */
  target: EmailOrderInvoicesTarget | null;
  /** False while the target may still resolve, so a refresh waits before closing the dialog. */
  isTargetSettled: boolean;
}

/**
 * Routes `EmailOrderInvoicesDialog` as `{base}/email-order-invoices/{orderKey}`, so Back closes
 * it and a refresh reopens it. A link to an order the host would not offer to email closes
 * without a message once the target settles.
 */
export function useRoutedEmailOrderInvoicesDialog({
  basePath,
  target,
  isTargetSettled,
}: UseRoutedEmailOrderInvoicesDialogOptions): {
  open: (orderKey?: string) => void;
  dialog: ReactNode;
  isOpen: boolean;
} {
  const [isDirty, setIsDirty] = useState(false);
  const routed = useRoutedDialog({
    path: DIALOG_PATH,
    basePath,
    hasFlowState: isTargetSettled ? target != null : undefined,
    isDirty,
  });

  // Keep the form mounted while the non-data-router Back guard restores history.
  const [lastTarget, setLastTarget] = useState(target);
  const isOpen = routed.isOpen && (target != null || (isDirty && lastTarget != null));

  // Keeps the order's details in the dialog while it animates closed.
  if (isOpen && target && !isSameTarget(target, lastTarget)) {
    setLastTarget(target);
  }
  const shown = isOpen ? (target ?? lastTarget) : lastTarget;

  const { open: openRoute } = routed;
  const open = useCallback(
    (orderKey?: string) => openRoute(orderKey ? { orderKey } : undefined),
    [openRoute],
  );

  return {
    open,
    isOpen,
    dialog: (
      <>
        <EmailOrderInvoicesDialog
          open={isOpen}
          onClose={routed.close}
          salesOrder={shown?.salesOrder}
          biOrderId={shown?.biOrderId}
          custPONumber={shown?.custPONumber}
          onDirtyChange={setIsDirty}
        />
        {routed.discardPrompt}
      </>
    ),
  };
}
